import React, { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import Card from './Card';
import { WordPopover } from '../WordPopover';
import Modal from '../Modal';
import { getRandomMemoryWords } from './memoryGameWords';
import { fetchImageWithFallback } from '../../services/imageService';
import { fetchSavedWords } from '../../services/vocabularyService';
import { flagImageMatch, getFlaggedUrlsForWord } from '../../services/flaggedImagesService';
import { updateUserMemoryScores } from '../../services/leaderboardService';
import FireworksReward from '../FireworksReward';
import Button from '../common/Button';
import { RefreshCw, ArrowLeft } from 'lucide-react';
import { Difficulty } from '../../pages/MemoryMatchPage';
import { useAuth } from '../../contexts/AuthContext';
import { API_BASE_URL } from '../../config/api';

interface CardData {
  id: number;
  type: 'word' | 'image';
  content: string;
  matchId: number;
  wordString: string;
}

interface GameBoardProps {
  difficulty: Difficulty;
  settings: {
    pairs: number;
  };
  onBack: () => void;
}

const GameBoard: React.FC<GameBoardProps> = ({ difficulty, settings, onBack }) => {
  const { currentUser } = useAuth();

  const [cards, setCards] = useState<CardData[]>([]);
  const [flippedCards, setFlippedCards] = useState<number[]>([]);
  const [matchedPairs, setMatchedPairs] = useState<number[]>([]);
  const [moves, setMoves] = useState(0);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [isGameOver, setIsGameOver] = useState(false);
  const [score, setScore] = useState(0);
  const [showScoreOverlay, setShowScoreOverlay] = useState(false);

  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const timerRef = useRef<number | null>(null);

  // Track how many pairs actually made it into the deck (in case some images fail)
  const [totalPairs, setTotalPairs] = useState<number>(settings.pairs);

  // Hint System State
  const [popoverWord, setPopoverWord] = useState<string | null>(null);
  const [popoverPosition, setPopoverPosition] = useState<{ top: number; left: number } | null>(null);
  const [hasSeenWarning, setHasSeenWarning] = useState<boolean>(() => {
    return localStorage.getItem('memoryGameHintWarningSeen') === 'true';
  });
  const [showWarningModal, setShowWarningModal] = useState(false);
  const [hintsUsed, setHintsUsed] = useState(0);
  const [pendingHintWord, setPendingHintWord] = useState<{ word: string; position: { top: number; left: number } } | null>(null);

  const startTimer = () => {
    if (timerRef.current) return;
    timerRef.current = window.setInterval(() => {
      setElapsedSeconds((prev) => prev + 1);
    }, 1000);
  };

  const stopTimer = () => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  };

  const resetTimer = () => {
    stopTimer();
    setElapsedSeconds(0);
  };

  const initializeGame = useCallback(async () => {
    setLoading(true);
    setError(null);
    setIsGameOver(false);
    setScore(0);
    setShowScoreOverlay(false);

    resetTimer();

    try {
      let savedWords: string[] = [];
      if (currentUser) {
        const userSaved = await fetchSavedWords(currentUser.uid);
        // Prioritize words that need practice or shuffle
        savedWords = userSaved
          .filter(w => typeof w.word === 'string')
          .sort(() => Math.random() - 0.5)
          .map(w => w.word);
      }

      // We want `settings.pairs` total words
      // Let's aim for 50% saved words, 50% curated new words
      const targetSavedCount = Math.ceil(settings.pairs / 2);
      const selectedSavedWords = savedWords.slice(0, targetSavedCount);

      const neededCuratedCount = settings.pairs - selectedSavedWords.length;
      const curatedWords = getRandomMemoryWords(neededCuratedCount + 5, selectedSavedWords); // extra in case of image failure

      let gameWordsPool = [...selectedSavedWords, ...curatedWords];
      // ensure we don't have duplicates
      gameWordsPool = Array.from(new Set(gameWordsPool));

      const initialCards: CardData[] = [];
      let includedPairs = 0;

      for (const word of gameWordsPool) {
        if (includedPairs >= settings.pairs) break;

        const skipUrls = await getFlaggedUrlsForWord(word);
        const imageResult = await fetchImageWithFallback(word, skipUrls);

        if (imageResult) {
          const pairId = includedPairs; // continuous matchId for included pairs
          initialCards.push({
            id: pairId * 2,
            type: 'word',
            content: word,
            matchId: pairId,
            wordString: word,
          });
          initialCards.push({
            id: pairId * 2 + 1,
            type: 'image',
            content: imageResult.url,
            matchId: pairId,
            wordString: word,
          });
          includedPairs += 1;
        } else {
          console.warn(`Could not fetch image for ${word}. It will be excluded.`);
        }
      }

      if (includedPairs === 0) {
        throw new Error('Could not initialize any cards. Check image fetching service.');
      }

      const shuffledCards = initialCards.sort(() => Math.random() - 0.5);
      setCards(shuffledCards);
      setTotalPairs(includedPairs);

      setMoves(0);
      setFlippedCards([]);
      setMatchedPairs([]);
      setHintsUsed(0);
      setPopoverWord(null);

      startTimer();
    } catch (err) {
      console.error('Failed to initialize game:', err);
      setError('Não foi possível carregar o jogo. Tente novamente mais tarde.');
    } finally {
      setLoading(false);
    }
  }, [settings.pairs]);

  // Compute score and finalize
  const handleGameOver = useCallback(() => {
    stopTimer();

    const difficultyMultiplier: Record<Difficulty, number> = {
      easy: 1,
      medium: 1.5,
      hard: 2,
    };

    // expected time per pair (seconds) increases with difficulty
    const expectedTimePerPair: Record<Difficulty, number> = {
      easy: 6,
      medium: 8,
      hard: 10,
    };

    const expectedTotalTime = expectedTimePerPair[difficulty] * totalPairs;
    const timeOver = Math.max(0, elapsedSeconds - expectedTotalTime);

    const movePenalty = moves * 3; // 3 points per move
    const timePenalty = Math.round(timeOver * 0.5); // 0.5 point per extra second
    const hintPenalty = hintsUsed * 5; // 5 points per hint
    const baseScore = 100 * difficultyMultiplier[difficulty];

    const calculatedScore = Math.max(10, Math.round(baseScore - movePenalty - timePenalty - hintPenalty));

    setScore(calculatedScore);
    setIsGameOver(true);
    setShowScoreOverlay(true);

    if (currentUser) {
      updateUserMemoryScores(currentUser.uid, calculatedScore, currentUser);
    }
  }, [moves, difficulty, currentUser, elapsedSeconds, totalPairs]);

  // Initialize / restart when settings or difficulty change
  useEffect(() => {
    initializeGame();
  }, [initializeGame, difficulty]);

  const handleFlag = useCallback(async (word: string, url: string) => {
    if (currentUser) {
      await flagImageMatch(word, url, 'user-flagged', currentUser.uid);
      alert('A imagem foi reportada e não será mostrada novamente para esta palavra.');
    }
  }, [currentUser]);

  // Safety: clear timer on unmount
  useEffect(() => {
    return () => {
      stopTimer();
    };
  }, []);

  const handleRewardComplete = useCallback(() => {
    // Close overlay and re-enable UI so user can click "Novo Jogo"
    setShowScoreOverlay(false);
    setIsGameOver(false);
  }, []);

  const handleShowHint = useCallback((e: React.MouseEvent, word: string) => {
    e.stopPropagation();
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
    // Position slightly below and right of the button
    const position = { top: rect.bottom + 5, left: Math.max(16, rect.left - 50) };

    if (!hasSeenWarning) {
      setPendingHintWord({ word, position });
      setShowWarningModal(true);
    } else {
      setPopoverWord(word);
      setPopoverPosition(position);
      setHintsUsed(prev => prev + 1);
    }
  }, [hasSeenWarning]);

  const confirmHint = () => {
    localStorage.setItem('memoryGameHintWarningSeen', 'true');
    setHasSeenWarning(true);
    setShowWarningModal(false);
    if (pendingHintWord) {
      setPopoverWord(pendingHintWord.word);
      setPopoverPosition(pendingHintWord.position);
      setHintsUsed(prev => prev + 1);
      setPendingHintWord(null);
    }
  };

  const handleCardClick = (index: number) => {
    if (
      loading ||
      isGameOver ||
      flippedCards.length === 2 ||
      flippedCards.includes(index) ||
      matchedPairs.includes(cards[index].matchId)
    ) {
      return;
    }

    const newFlippedCards = [...flippedCards, index];
    setFlippedCards(newFlippedCards);

    if (newFlippedCards.length === 2) {
      setMoves((m) => m + 1);

      const card1 = cards[newFlippedCards[0]];
      const card2 = cards[newFlippedCards[1]];

      if (card1.matchId === card2.matchId) {
        const newMatchedPairs = [...matchedPairs, card1.matchId];
        setMatchedPairs(newMatchedPairs);
        setFlippedCards([]);

        if (newMatchedPairs.length === totalPairs) {
          handleGameOver();
        }
      } else {
        setTimeout(() => {
          setFlippedCards([]);
        }, 1200);
      }
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-purple-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-64 text-red-500">
        <p className="text-xl">{error}</p>
        <Button onClick={initializeGame} className="mt-4" leftIcon={<RefreshCw />}>
          Tentar Novamente
        </Button>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center">
      <Modal
        isOpen={showWarningModal}
        setIsModalOpen={setShowWarningModal}
        onClose={() => setPendingHintWord(null)}
        title="Aviso de Dica"
        message="Ver a definição da palavra custará 5 pontos do seu placar final. Deseja continuar?"
        actionText="Sim, usar dica"
        action={confirmHint}
      />

      {popoverWord && popoverPosition && createPortal(
        <WordPopover
          word={popoverWord}
          position={popoverPosition}
          onClose={() => setPopoverWord(null)}
        />,
        document.body
      )}

      {isGameOver && showScoreOverlay && (
        <FireworksReward onComplete={handleRewardComplete} points={score} />
      )}

      <div className="flex flex-col sm:flex-row justify-between items-center w-full max-w-4xl mb-8 gap-4 px-4">
        <Button onClick={onBack} variant="outline" leftIcon={<ArrowLeft />}>
          Mudar Dificuldade
        </Button>

        <div className="flex items-center gap-6">
          <div className="text-xl font-semibold text-gray-700">Movimentos: {moves}</div>
          <div className="text-xl font-semibold text-gray-700">
            Tempo:{' '}
            {String(Math.floor(elapsedSeconds / 60)).padStart(2, '0')}:
            {String(elapsedSeconds % 60).padStart(2, '0')}
          </div>
        </div>

        <Button onClick={initializeGame} disabled={loading} leftIcon={<RefreshCw />}>
          {loading ? 'Carregando...' : 'Novo Jogo'}
        </Button>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-4 mb-8">
        {cards.map((card, index) => (
          <Card
            key={card.id}
            content={card.content}
            type={card.type}
            isFlipped={flippedCards.includes(index) || matchedPairs.includes(card.matchId)}
            isMatched={matchedPairs.includes(card.matchId)}
            onClick={() => handleCardClick(index)}
            onFlag={card.type === 'image' ? () => handleFlag(card.wordString, card.content) : undefined}
            onShowHint={card.type === 'word' && (flippedCards.includes(index) || matchedPairs.includes(card.matchId)) ? handleShowHint : undefined}
          />
        ))}
      </div>
    </div>
  );
};

export default GameBoard;
